import {
	Alert,
	Button,
	Modal,
	Space,
	Table,
	Tag,
	Typography,
	Upload,
} from "antd";
import type { UploadProps } from "antd";
import { ColumnsType } from "antd/es/table";
import { useState } from "react";
import {
	downloadLessonLearnedImportTemplate,
	importLessonLearnedFromExcel,
} from "../../services/api-server/lesson_learned";
import { DownloadOutlined } from "@ant-design/icons";

const { Dragger } = Upload;
const { Text, Title } = Typography;

interface FieldError {
	column: string;
	value: string;
	message: string;
}

interface RowError {
	row: number;
	fields: FieldError[];
}

interface ImportSummary {
	totalRows: number;
	validRows: number;
	invalidRows: number;
}

interface ImportResult {
	success: boolean;
	importedCount?: number;
	summary?: ImportSummary;
	errors?: RowError[];
}

interface LessonLearnedImportModalProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: (importedCount: number) => void;
	tenantKey?: string;
	operationContext?: {
		operation_id: string;
		project_code: string;
		well_id: string;
	};
}

function LessonLearnedImportModal({
	open,
	onClose,
	onSuccess,
	tenantKey,
	operationContext,
}: LessonLearnedImportModalProps) {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
	const [isImporting, setIsImporting] = useState(false);
	const [importResult, setImportResult] = useState<ImportResult | null>(null);
	const [importError, setImportError] = useState<string | null>(null);

	const handleDownloadTemplate = async () => {
		setIsDownloadingTemplate(true);
		try {
			await downloadLessonLearnedImportTemplate(tenantKey, operationContext);
		} catch (err) {
			setImportError("Failed to download template. Please try again.");
		} finally {
			setIsDownloadingTemplate(false);
		}
	};

	const handleImport = async () => {
		if (!selectedFile) return;

		setIsImporting(true);
		setImportResult(null);
		setImportError(null);

		try {
			const result = await importLessonLearnedFromExcel(
				selectedFile,
				tenantKey,
				operationContext
			);
			setImportResult(result);
			if (result.success) {
				onSuccess?.(result.importedCount ?? 0);
			}
		} catch (err: any) {
			if (err?.success === false && err?.errors) {
				setImportResult(err as ImportResult);
			} else if (err?.detail) {
				setImportError(err.detail);
			} else if (typeof err === "string") {
				setImportError(err);
			} else {
				setImportError("Import failed. Please check the file and try again.");
			}
		} finally {
			setIsImporting(false);
		}
	};

	const handleClose = () => {
		setSelectedFile(null);
		setImportResult(null);
		setImportError(null);
		onClose();
	};

	const uploadProps: UploadProps = {
		name: "file",
		multiple: false,
		maxCount: 1,
		accept:
			".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		beforeUpload: (file) => {
			const isXlsx =
				file.type ===
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
				file.name.toLowerCase().endsWith(".xlsx");
			if (!isXlsx) {
				setImportError("Only .xlsx files are supported.");
				return Upload.LIST_IGNORE;
			}
			const isUnder5MB = file.size / 1024 / 1024 < 5;
			if (!isUnder5MB) {
				setImportError("File must be smaller than 5 MB.");
				return Upload.LIST_IGNORE;
			}
			setSelectedFile(file);
			setImportResult(null);
			setImportError(null);
			return false;
		},
		onRemove: () => {
			setSelectedFile(null);
			setImportResult(null);
			setImportError(null);
		},
		fileList: selectedFile
			? [
					{
						uid: "-1",
						name: selectedFile.name,
						status: "done",
						size: selectedFile.size,
					},
				]
			: [],
	};

	const errorColumns: ColumnsType<RowError> = [
		{
			title: "Row",
			dataIndex: "row",
			key: "row",
			width: 70,
			render: (row: number) => <Tag color="red">{row}</Tag>,
		},
		{
			title: "Errors",
			dataIndex: "fields",
			key: "fields",
			render: (fields: FieldError[]) => (
				<ul style={{ margin: 0, paddingLeft: 16 }}>
					{fields.map((f, idx) => (
						<li key={idx}>
							<Text strong>{f.column}</Text>
							{f.value ? <Text type="secondary"> ("{f.value}")</Text> : null}
							{" — "}
							<Text type="danger">{f.message}</Text>
						</li>
					))}
				</ul>
			),
		},
	];

	const hasErrors =
		importResult && !importResult.success && importResult.errors?.length;

	return (
		<Modal
			title={"Import Lessons Learned from Excel"}
			open={open}
			onCancel={handleClose}
			width={760}
			footer={null}
			destroyOnClose
		>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				{/* Step 1: Download template */}
				<div
					style={{
						padding: "16px",
						border: "1px solid var(--ant-color-border, #d9d9d9)",
						borderRadius: "8px",
					}}
				>
					<Title level={5} style={{ margin: "0 0 8px" }}>
						Step 1 — Download the template
					</Title>
					<Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
						Download the Excel template, fill in your data starting from row 4
						(rows 2–3 are examples), and save the file.
					</Text>
					<Button
						onClick={handleDownloadTemplate}
						loading={isDownloadingTemplate}
						icon={<DownloadOutlined />}
					>
						Download Template
					</Button>
				</div>

				{/* Step 2: Upload */}
				<div
					style={{
						padding: "16px",
						border: "1px solid var(--ant-color-border, #d9d9d9)",
						borderRadius: "8px",
					}}
				>
					<Title level={5} style={{ margin: "0 0 8px" }}>
						Step 2 — Upload the filled file
					</Title>
					<Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
						Only <Text code>.xlsx</Text> files up to 5 MB are accepted. Maximum
						10,000 data rows per file.
					</Text>
					<Dragger {...uploadProps} style={{ borderRadius: "6px" }}>
						<p className="ant-upload-drag-icon">📂</p>
						<p className="ant-upload-text">
							Click or drag your .xlsx file here to upload
						</p>
						<p className="ant-upload-hint">
							Supports single .xlsx file upload only
						</p>
					</Dragger>
				</div>

				{/* Import error (non-validation) */}
				{importError && (
					<Alert
						type="error"
						message="Import Error"
						description={importError}
						showIcon
						closable
						onClose={() => setImportError(null)}
					/>
				)}

				{/* Success state */}
				{importResult?.success && (
					<Alert
						type="success"
						message={`${importResult.importedCount} lesson${importResult.importedCount !== 1 ? "s" : ""} learned imported successfully.`}
						showIcon
					/>
				)}

				{/* Validation errors */}
				{hasErrors && (
					<div>
						<Alert
							type="warning"
							message={`Validation failed — ${importResult!.errors!.length} row(s) have errors`}
							description={
								<span>
									{importResult!.summary?.validRows ?? 0} of{" "}
									{importResult!.summary?.totalRows ?? 0} rows are valid. Fix
									the issues below and re-upload.
								</span>
							}
							showIcon
							style={{ marginBottom: 12 }}
						/>
						<Table<RowError>
							columns={errorColumns}
							dataSource={(importResult!.errors ?? []).map((e) => ({
								...e,
								key: e.row,
							}))}
							size="small"
							pagination={{ pageSize: 10, showSizeChanger: false }}
							scroll={{ y: 300 }}
						/>
					</div>
				)}

				{/* Footer actions */}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
					<Button onClick={handleClose}>Cancel</Button>
					<Button
						type="primary"
						onClick={handleImport}
						disabled={!selectedFile || isImporting}
						loading={isImporting}
					>
						Import
					</Button>
				</div>
			</Space>
		</Modal>
	);
}

export default LessonLearnedImportModal;
